import * as Phaser from "phaser";
import { AvailabilityStatus } from "@workadventure/messages";
import type { GameScene } from "../Game/GameScene";
import { waScaleManager, WaScaleManagerEvent } from "../Services/WaScaleManager";
import { UsernameMegaphoneDisplay } from "./UsernameMegaphoneDisplay";
import { UsernameStatusDisplay } from "./UsernameStatusDisplay";

const CORRECTION_RATE = 0.65; // When one game pixel is smaller than one screen pixel (zoomed-out), we zoom out the Woka name, but only up to CORRECTION_RATE. After that, the Woka name will stay at its current screen size even if we zoom out more (to keep the text readable)
const USERNAME_FONT_FAMILY = "Roboto";
const USERNAME_FONT_SIZE = 10;
const USERNAME_FONT_WEIGHT = 500;
const USERNAME_SIZE_ANIMATION_DURATION = 375;
const USERNAME_SIZE_ANIMATION_EASING = "cubic-bezier(0.2, 0, 0, 1)";

const PLAYER_NAME_BACKGROUND_COLOR = "rgba(27, 42, 65, 0.5)";
const PLAYER_NAME_BACKGROUND_RADIUS = 8;
const PLAYER_NAME_HEIGHT = 14;
const PLAYER_NAME_RESTING_HEIGHT = 24;
const PLAYER_NAME_PADDING = 4;
const PLAYER_NAME_GAP = 4;

type Position = { x: number; y: number };

export class UsernameDisplay {
    private static nextDomUsernameId = 0;
    private readonly domUsernameId = UsernameDisplay.nextDomUsernameId++;
    private readonly element: HTMLDivElement;
    private readonly playerNameElement: HTMLParagraphElement;
    private readonly playerNameContainer: HTMLDivElement;
    private readonly restingElement: HTMLParagraphElement;
    private readonly playerName: string;
    private playerNameOutlineColor: number | undefined;
    private displayScale: number;
    private readonly statusDisplay: UsernameStatusDisplay;
    private readonly megaphoneDisplay: UsernameMegaphoneDisplay;
    private sizeAnimation: Animation | undefined;
    private kimaiTracking: boolean | undefined;

    private readonly onZoomChanged = (zoomModifier: number): void => {
        this.displayScale = this.getDisplayScale(zoomModifier);
        // The ancestor scale we cancel in applyStyles()/applyTransform() is measured from the DOM
        // layer, so invalidate it and re-apply at render time, once Phaser has updated the layer's
        // transform for the new zoom. Applying immediately would measure the previous frame's scale.
        this.gameScene.usernameDomLayer.invalidateAncestorScale();
        this.scene.events.once(Phaser.Scenes.Events.RENDER, () => {
            this.applyStyles();
        });
    };
    private toForeFront: boolean = false;
    private depth: number = 0;

    constructor(
        private scene: GameScene,
        private x: number,
        private y: number,
        playerName: string,
        outlineColor: number | undefined,
    ) {
        this.playerName = playerName;
        this.displayScale = this.getDisplayScale(waScaleManager.zoomModifier);

        this.playerNameOutlineColor = outlineColor;
        this.statusDisplay = new UsernameStatusDisplay();
        this.megaphoneDisplay = new UsernameMegaphoneDisplay();
        this.playerNameElement = this.createPlayerNameElement();

        this.playerNameContainer = this.createPlayerNameContainer();
        this.restingElement = this.createRestingElement();

        this.playerNameContainer.append(
            this.playerNameElement,
            this.restingElement,
        );

        this.element = document.createElement("div");

        this.element.ariaHidden = "true";
        this.element.className = "username-display";
        this.element.style.background = PLAYER_NAME_BACKGROUND_COLOR;

        this.applyStyles();
        this.updatePlayerDepth();
        this.element.append(
            this.statusDisplay.element,
            this.playerNameContainer,
            this.megaphoneDisplay.element,
        );
        this.updateUsernameBackgroundColor(outlineColor);

        this.gameScene.usernameDomLayer.addUsername(this.element);

        // The immediate applyStyles() above may have measured the ancestor scale before Phaser
        // applied the layer transform for this frame. Re-apply once at render time so the initial
        // scale is measured against the real on-screen transform.
        this.gameScene.usernameDomLayer.invalidateAncestorScale();
        this.scene.events.once(Phaser.Scenes.Events.RENDER, () => {
            this.applyStyles();
        });

        this.scene.game.events.on(WaScaleManagerEvent.ZoomChanged, this.onZoomChanged);
    }

    private getDisplayScale(zoomModifier: number): number {
        return Math.max(zoomModifier > 0 ? CORRECTION_RATE / zoomModifier : 1, 1);
    }

    private showMegaphone(show = true, forceClose = false): void {
        if (show === this.megaphoneDisplay.isShown()) {
            return;
        }
        this.animateSizeChange(() => {
            this.megaphoneDisplay.show(show, forceClose);
        }, show);
    }

    public setPlayerNameOutlineColor(outlineColor: number | undefined): void {
        if (this.playerNameOutlineColor === outlineColor) {
            return;
        }
        this.playerNameOutlineColor = outlineColor;

        this.updateUsernameBackgroundColor(outlineColor);
    }

    public setAvailabilityStatus(availabilityStatus: AvailabilityStatus, instant = false, forceClose = false): void {
        this.statusDisplay.setAvailabilityStatus(availabilityStatus, instant);
        this.showMegaphone(availabilityStatus === AvailabilityStatus.SPEAKER, forceClose);
    }

    /**
     * Estado independiente del availabilityStatus de WorkAdventure.
     *
     * false     -> mostrar "Descansando"
     * true      -> ocultar
     * undefined -> ocultar
     */
    public setKimaiTracking(kimaiTracking: boolean | undefined): void {
        if (this.kimaiTracking === kimaiTracking) {
            return;
        }

        this.kimaiTracking = kimaiTracking;

        this.restingElement.style.display =
            kimaiTracking === false
                ? "block"
                : "none";

        this.applyStyles();
    }

    public getAvailabilityStatus(): AvailabilityStatus {
        return this.statusDisplay.availabilityStatus;
    }

    public setPlayerDepth(depth: number): void {
        this.depth = depth;
        this.updatePlayerDepth();
    }

    private updatePlayerDepth(): void {
        this.element.style.zIndex = `${Math.round(this.depth + (this.toForeFront ? 2000000000 : 0))}`;
    }

    public setPosition(x: number, y: number): this {
        if (x === this.x && y === this.y) {
            return this;
        }

        this.x = x;
        this.y = y;
        this.updateDomPosition();
        return this;
    }

    public destroy(): void {
        this.stopSizeAnimation();
        this.element.remove();
        this.statusDisplay.destroy();
        this.megaphoneDisplay.destroy();
        this.scene.game.events.off(WaScaleManagerEvent.ZoomChanged, this.onZoomChanged);
    }

    private get gameScene(): GameScene {
        return this.scene;
    }

    private updateDomPosition(): void {
        this.scene.events.once(Phaser.Scenes.Events.RENDER, () => {
            this.applyTransform();
        });
    }

    private getDomPosition(): Position {
        const height =
            this.kimaiTracking === false
                ? PLAYER_NAME_RESTING_HEIGHT
                : PLAYER_NAME_HEIGHT;

        return {
            x: this.x,
            y: this.y - (height / 2) * this.displayScale,
        };
    }

    private animateSizeChange(changeElement: () => void, megaphoneShownAfterChange: boolean): void {
        this.stopSizeAnimation();

        const previousWidth = this.element.offsetWidth;
        changeElement();
        const nextWidth = megaphoneShownAfterChange ? this.element.offsetWidth : this.measureWidthWithoutMegaphone();

        if (previousWidth === nextWidth) {
            return;
        }

        this.element.style.overflow = "hidden";
        this.element.style.width = `${previousWidth}px`;

        this.sizeAnimation = this.element.animate([{ width: `${previousWidth}px` }, { width: `${nextWidth}px` }], {
            duration: USERNAME_SIZE_ANIMATION_DURATION,
            easing: USERNAME_SIZE_ANIMATION_EASING,
        });

        this.sizeAnimation.onfinish = () => {
            this.sizeAnimation = undefined;
            this.element.style.overflow = "";
            this.element.style.width = "";
        };
    }

    private stopSizeAnimation(): void {
        this.sizeAnimation?.cancel();
        this.sizeAnimation = undefined;
        this.element.style.overflow = "";
        this.element.style.width = "";
    }

    private measureWidthWithoutMegaphone(): number {
        const previousDisplay = this.megaphoneDisplay.element.style.display;

        this.megaphoneDisplay.element.style.display = "none";
        const width = this.element.offsetWidth;
        this.megaphoneDisplay.element.style.display = previousDisplay;

        return width;
    }

    private createPlayerNameElement(): HTMLParagraphElement {
        const element = document.createElement("p");

        element.textContent = this.playerName;
        element.style.margin = "0";
        element.style.marginRight = `calc(2px * var(--username-dom-scale, 1))`;
        element.style.color = "#ffffff";
        element.style.fontFamily = USERNAME_FONT_FAMILY;
        element.style.fontSize = `calc(${USERNAME_FONT_SIZE}px * var(--username-dom-scale, 1))`;
        element.style.fontWeight = `${USERNAME_FONT_WEIGHT}`;
        element.style.whiteSpace = "nowrap";
        element.style.pointerEvents = "none";

        return element;
    }

    private createPlayerNameContainer(): HTMLDivElement {
        const element = document.createElement("div");

        element.style.display = "flex";
        element.style.flexDirection = "column";
        element.style.alignItems = "flex-start";
        element.style.justifyContent = "center";
        element.style.pointerEvents = "none";
        element.style.lineHeight = "1";

        return element;
    }

    private createRestingElement(): HTMLParagraphElement {
        const element = document.createElement("p");

        element.textContent = "Descansando";
        element.style.display = "none";
        element.style.margin = "0";
        element.style.marginTop =
            "calc(2px * var(--username-dom-scale, 1))";

        element.style.color =
            "rgba(255, 255, 255, 0.58)";

        element.style.fontFamily =
            USERNAME_FONT_FAMILY;

        element.style.fontSize =
            "calc(8px * var(--username-dom-scale, 1))";

        element.style.fontWeight = "400";
        element.style.fontStyle = "italic";
        element.style.whiteSpace = "nowrap";
        element.style.pointerEvents = "none";

        return element;
    }

    /**
     * If true, the z-index is updated to be at the very top. Used when we hover over a Woka.
     */
    public setToForeFront(toForeFront: boolean): void {
        this.toForeFront = toForeFront;
        this.updatePlayerDepth();
    }

    /**
     * The scale applied through the element's own CSS `transform`.
     *
     * This element is a promoted compositing layer (`will-change: transform` + `translate3d`). Safari
     * rasterizes such a layer's backing store at full resolution ONLY when the element's own
     * transform is a *minification* (scale < 1); when the own transform is a pure translation
     * (scale === 1) it caches the bitmap at `devicePixelRatio` and lets the parent transform stretch
     * it — blurry on a low-DPI screen, but fine on a 2× Retina screen whose backing store is already
     * twice as dense. That is exactly why the name was blurry at a medium zoom only on the 2K
     * monitor.
     *
     * So we cancel the parent DOM layer's on-screen scale here to bring the layer's total scale to
     * ≤ 1, which makes the own transform a genuine minification whenever the parent magnifies. We
     * MEASURE that parent scale from the DOM (`getAncestorScale`) rather than deriving it from
     * `zoomModifier`/`actualZoom`, because the real value depends on the camera zoom and the device
     * pixel ratio together and does not match either of those numbers.
     *
     * Clamp at 1 so we never magnify: a scale ≥ 1 is the pure-translate/upscale case that blurs on
     * Safari. When the parent is already minifying (ancestorScale < 1, e.g. zoomed out) the clamp
     * leaves the own transform at 1, which is fine — minifying a bitmap stays sharp everywhere.
     */
    private getTransformScale(): number {
        return Math.min(1 / this.gameScene.usernameDomLayer.getAncestorScale(), 1);
    }

    private applyTransform(): void {
        const scale = this.getTransformScale();
        const position = this.getDomPosition();
        this.element.style.transform = `translate3d(${position.x}px, ${position.y}px, 0) translate(-50%, -50%) scale(${scale})`;
    }

    private getDomScale(): number {
        // Layout scale for the name (font-size, padding, …). Dividing by the transform scale folds
        // the part of the zoom compensation that getTransformScale() cannot apply without magnifying
        // into the layout size, so the element is laid out — and therefore rasterized — at full
        // resolution. `displayScale / transformScale` keeps the final rendered size independent of
        // the split: the parent Phaser DOM layer re-applies `ancestorScale`, which cancels the
        // `1/ancestorScale` the transform removed, leaving the rendered size at
        // `displayScale * ancestorScale` — unchanged from before this fix.
        return this.displayScale / this.getTransformScale();
    }

    private applyStyles(): void {
        const domScale = this.getDomScale();

        const height =
            this.kimaiTracking === false
                ? PLAYER_NAME_RESTING_HEIGHT
                : PLAYER_NAME_HEIGHT;

        this.element.style.setProperty(
            "--username-dom-scale",
            domScale.toString(),
        );

        this.element.style.height =
            `${height * domScale}px`;

        this.element.style.gap =
            `${PLAYER_NAME_GAP * domScale}px`;

        this.element.style.padding =
            `0 ${PLAYER_NAME_PADDING * domScale}px`;

        this.element.style.borderRadius =
            `${PLAYER_NAME_BACKGROUND_RADIUS * domScale}px`;

        this.applyTransform();
    }

    private updateUsernameBackgroundColor(color: number | undefined): void {
        this.element.style.background =
            color === undefined ? PLAYER_NAME_BACKGROUND_COLOR : `#${color.toString(16).padStart(6, "0")}`;
    }

    public setTalking(talking: boolean, isSpeaker: boolean): void {
        if (talking) {
            this.element.classList.add(isSpeaker ? "speaker" : "talking");
        } else {
            this.element.classList.remove("talking");
            this.element.classList.remove("speaker");
        }
    }
}
